/**
 * Helpers for preview image loading strategy.
 *
 * Used by PreviewCloudinaryImage to decide whether to render images immediately
 * or wrap them in Lazy (skeleton placeholder). Local init assets and init-preview
 * mode should load eagerly to avoid slow or stuck placeholders on cached images.
 */

const STATIC_PREVIEW_ASSET_PREFIXES = ["/images/phone-previews/", "/images/previews/"] as const;

const IMAGE_URL_PATTERN = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i;

export const isStaticPreviewAsset = (src?: string): boolean => {
  if (!src) {
    return false;
  }

  return STATIC_PREVIEW_ASSET_PREFIXES.some((prefix) => src.startsWith(prefix));
};

export const shouldEagerLoadPreviewImage = (src?: string, isInitPreview = false): boolean => {
  return isInitPreview || isStaticPreviewAsset(src);
};

const isLikelyImageUrl = (value: string): boolean => {
  return (
    isStaticPreviewAsset(value) || value.includes("cloudinary.com") || IMAGE_URL_PATTERN.test(value)
  );
};

/**
 * Recursively extracts image URLs from init preview JSON data.
 */
export const extractPreviewImageUrls = (data: unknown): string[] => {
  const urls = new Set<string>();

  const walk = (value: unknown): void => {
    if (typeof value === "string" && isLikelyImageUrl(value)) {
      urls.add(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }

    if (value && typeof value === "object") {
      Object.values(value).forEach(walk);
    }
  };

  walk(data);
  return [...urls];
};

/**
 * Preloads preview images in the background so they are cached before render.
 */
export const preloadPreviewImages = (urls: string[]): void => {
  if (typeof window === "undefined") {
    return;
  }

  urls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
};
